<template>
	<leaflet-layer-group ref="layerGroup"/>
</template>

<script>
import SuperCluster from 'supercluster';
import { mapDynamicActions, mapDynamicState, mapDynamicGetters } from '@/core/mapDynamic';
import createIconMixin from '@/components/map/CreateIcon.mixin.js';
import {
	eventBus, statuses
} from '@/core';

export default {
	name: 'MapOrders',
	inject: [ 'leaflet', 'state', 'namespace', 'pane' ],
	mixins: [createIconMixin],
	props: {
		modal: { default: null, type: HTMLElement },
		iconOptions: { type: Object },
		tripDetailView: { type: Boolean }
	},
	data() {
		return {
			createdMarkers: [],
			spiderMarkers: [],
			spiderLines: [],
			selectedMarkers: [],
			mapCluster: null,
			clusterPoints: {},
			dragging: false,
			spiderified: [],
			spiderifiedLines: [],
			lastBounds: null,
			mapAdjustTimeout: null,
			orderIcon: null,
			terminalIcon: null,
			clonedNode: null,
			draggedIndex: null,
			ready: false,
			terminals: [],
			validTerminals: [],
			mapLoadTimeout: null,
			mapLoadDelay: 500
		};
	},
	computed: {
		computedStoreNamespace() {
			return `loadPlanning/${this.namespace.id}`;
		},
		computedOrderStoreNamespace() {
			return `loadPlanning/${this.namespace.id}/orderSearch`;
		},
		...mapDynamicState('computedStoreNamespace',[
			'mapState',
			'showDeliveriesInMap',
			'mapSelecting',
			'gridSelecting',
			'mapOrderIds',
			'selectedGridOrderIds',
			'filteredGridOrderIds',
			'selectedMapOrderIds',
		]),
		...mapDynamicGetters('computedStoreNamespace',['tripDetails']),
		...mapDynamicState('computedOrderStoreNamespace', [
			'orders',
			'preloadedOrderData',
			'ordersLoading',
			'ordersFetching'
		]),
		orderData() {
			if (this.tripDetailView) return this.tripDetails.orders || [];
			else return this.ordersLoading || this.ordersFetching ? this.preloadedOrderData : this.orders;
		},
		cluster: {
			get() {
				return this.mapState.cluster;
			},
			set(value) {
				if (this.mapState.cluster === value) return;
				this.setMapState({ key: 'cluster', value });
			}
		},
		tooltip: {
			get() {
				return this.mapState.tooltipVisible;
			},
			set(value) {
				if (this.mapState.tooltipVisible === value) return;
				this.setMapState({ key: 'tooltipVisible', value });
			}
		},
		clusterTooltip: {
			get() {
				return this.mapState.clusterTooltipVisible;
			},
			set(value) {
				if (this.mapState.clusterTooltipVisible === value) return;
				this.setMapState({ key: 'clusterTooltipVisible', value });
			}
		},
		popupComponent: {
			get() {
				return this.mapState.popup;
			},
			set(value) {
				if (this.mapState.popup === value) return;
				this.setMapState({ key: 'popup', value });
			}
		},
		order: {
			get() {
				return this.mapState.selectedOrderNode;
			},
			set(value) {
				if (this.mapState.selectedOrderNode === value) return;
				this.setMapState({ key: 'selectedOrderNode', value });
			}
		},
		orderCluster: {
			get() {
				return this.mapState.orderCluster;
			},
			set(value) {
				if (this.mapState.orderCluster === value) return;
				this.setMapState({ key: 'orderCluster', value });
			}
		}
	},
	watch: {
		orders: {
			immediate: true,
			handler() {
				if (!this.ready || this.ordersFetching || this.ordersLoading || this.tripDetailView) return;
				this.setupDefaultClusterData();
				this.createNodes(true);
				this.processMapOrdersForStore(true);
			}
		},
		preloadedOrderData:{
			immediate: true,
			handler(newVal) {
				if (!this.ready || !newVal || newVal.length === 0) return;
				this.setupDefaultClusterData();
				this.createNodes(true);
				this.processMapOrdersForStore(true);
			}
		},
		modal() {
			if (!this.ready) return;
			this.createNodes();
		},
		selectedGridOrderIds: {
			immediate: true,
			handler(newVal, oldVal) {
				if (!this.ready || this.ordersLoading || this.ordersFetching) return;
				if ( (!oldVal && newVal && newVal.length === 0) ||
					(oldVal && newVal && oldVal.length === 0 && newVal.length === 0) ) return;
				if (this.checkArraysSame(newVal, oldVal)) return;
				this.createNodes(true);
			}
		},
		filteredGridOrderIds: {
			immediate: true,
			handler(newVal, oldVal) {
				if (!this.ready || this.ordersLoading || this.ordersFetching) return;
				if (this.checkArraysSame(newVal, oldVal)) return;
				this.createNodes(true);
			}
		},
		showDeliveriesInMap: {
			immediate: true,
			handler(newVal, oldVal) {
				if (!this.ready) return;
				if (newVal === oldVal) return;

				this.setupDefaultClusterData();
				this.createNodes(true);
				this.processMapOrdersForStore(true);
			}
		},
		tripDetailView: {
			immediate: true,
			handler(newVal, oldVal) {
				if (!this.ready) return;
				if (newVal === oldVal) return;

				this.setupDefaultClusterData();
				this.createNodes(true);
				this.processMapOrdersForStore(true);
			}
		},
		terminals: {
			immediate: true,
			handler(newVal, oldVal) {
				if (!this.ready) return;
				if ( (!oldVal && newVal && newVal.length === 0) ||
					(oldVal && newVal && oldVal.length === 0 && newVal.length === 0) ) return;
				this.setupTerminalPoints();
			}
		},
	},
	beforeDestroy() {
		this.cluster = null;
		this.popupComponent = null;
		this.tooltip = false;
		this.clusterTooltip = false;
		this.state.map.off('click', this.mapClick);
		this.state.map.off('moveend', this.mapMove);
		this.state.map.off('zoomend', this.mapZoom);
		//this.state.map.off('popupclose', this.mapChange);
	},
	async mounted() {
		this.orderIcon = this.createIcon({
			iconName: 'order',
			iconType: 'timpex',
			cluster: null
		});
		this.terminalIcon = this.createIcon({
			iconName: 'office',
			iconType: 'bp',
		});
		await this.$nextTick();
		this.state.map.on('moveend', this.mapMove);
		this.state.map.on('click', this.mapClick);
		this.state.map.on('zoomend', this.mapZoom);

		// these get called far too much on initial load
		this.terminals = await this.getTerminals();
		this.setupTerminalPoints();
		this.setupDefaultClusterData();
		this.createNodes(true);
		this.processMapOrdersForStore(true);
		this.ready = true;
	},
	methods: {
		...mapDynamicActions('computedStoreNamespace', [ 'ordersInMap', 'setSelectedMapOrderIds', 'setMapState', 'getTerminals', 'setTripDetails' ]),
		mapClick() {
			this.resetSelectedMarkers(true);
		},
		mapMove() {
			if (!this.ready) return;
			clearTimeout(this.mapAdjustTimeout);
			this.mapAdjustTimeout = setTimeout(() => {
				this.createNodes(false);
				this.processMapOrdersForStore();
			},50);
		},
		mapZoom() {
			if (!this.ready) return;
			clearTimeout(this.mapAdjustTimeout);
			this.mapAdjustTimeout = setTimeout(() => {
				this.createNodes(false);
				this.processMapOrdersForStore();
			},50);
		},
		zoomToCluster(marker, order) {
			if (this.state.map.getZoom() < 18) {
				this.state.map.setView(marker.getLatLng(), this.cluster.getClusterExpansionZoom(order.cluster_id));
			}
		},
		resetSelectedMarkers(clearOrders) {
			if (this.selectedMarkers.length > 0) {
				for (let selectedIndex of this.selectedMarkers) {
					let markerData = this.createdMarkers[selectedIndex];
					if (markerData) this.removeMarkerClass(markerData.marker, 'selected');
				}
				this.selectedMarkers = [];
				if (clearOrders && this.selectedMapOrderIds && this.selectedMapOrderIds.length > 0)
					this.setSelectedMapOrderIds([]);
			}
		},
		refreshSelectedOrders() {
			if (this.gridSelecting) return;
			let selectedIds = [];
			for (let selectedIndex of this.selectedMarkers) {
				let marker = this.createdMarkers[selectedIndex];
				if (marker) selectedIds = selectedIds.concat(marker.orderIds);
			}
			this.setSelectedMapOrderIds(selectedIds);
		},
		createPopup() {
			this.popupComponent = this.leaflet.popup({ closeButton: false, offset: [ -1, -10 ] }).setContent(this.modal);
		},
		clearPopupData() {
			this.tooltip = false;
			this.clusterTooltip = false;
			this.order = {};
			this.orderCluster = null;
			if (this.popupComponent) this.state.map.closePopup(this.popupComponent);
		},
		selectMapOrderIds(markerData) {
			if (!this.gridSelecting) {
				let orders = markerData.orderIds;
				this.setSelectedMapOrderIds(this.selectedMapOrderIds.concat(orders));
			}
		},
		setupDefaultClusterData() {
			this.clusterPoints = {
				type: 'FeatureCollection',
				features: []
			};

			this.mapCluster = new SuperCluster({
				radius: 40,
				maxZoom: 18,
				map: (p) => ({ orderIds: [p.id] }),
				reduce: (a, p) => {
					a.orderIds = [].concat(a.orderIds, p.orderIds);
				}
			});
			this.cluster = new SuperCluster({
				radius: 40,
				maxZoom: 18,
				map: (p) => ({
					delivery_count: p.deliveryMarker ? 1 : 0,
					pickup_count: p.deliveryMarker ? 0 : 1,
					orderIds: [p.id],
					terminals: []
				}),
				reduce: (a, p) => {
					a.delivery_count += p.delivery_count;
					a.pickup_count += p.pickup_count;
					a.orderIds = [].concat(a.orderIds, p.orderIds);
					a.terminals = a.orderIds.filter( (value) => this.validTerminals.some( ({ id }) => value === id));
				}
			});
			for (let order of this.orderData) {
				if (this.showDeliveriesInMap && order.deliveryParty && 'latitude' in order.deliveryParty && 'longitude' in order.deliveryParty) {
					this.clusterPoints.features.push({
						type: 'Feature',
						id: order.id,
						geometry: {
							type: 'Point',
							coordinates: [ order.deliveryParty.longitude, order.deliveryParty.latitude ]
						},
						properties: { id: order.id, orderId: order.orderId, deliveryMarker: true, status: order.status }
					});
				}

				if ( order.despatchParty && 'latitude' in order.despatchParty && 'longitude' in order.despatchParty ) {
					this.clusterPoints.features.push({
						type: 'Feature',
						id: order.id,
						geometry: {
							type: 'Point',
							coordinates: [ order.despatchParty.longitude, order.despatchParty.latitude ]
						},
						properties: { id: order.id, orderId: order.orderId, status: order.status }
					});
				}
			}

			if (!this.tripDetailView) {
				this.clusterPoints.features.push(...this.validTerminals);
			}
			this.mapCluster.load(this.clusterPoints.features);
		},
		processMapOrdersForStore(force) {
			if (this.gridSelecting) return;
			try {
				let { _northEast, _southWest } = this.state.map.getBounds();
			

				if (this.lastBounds &&
				this.lastBounds._southWest.lat === _southWest.lat &&
				this.lastBounds._southWest.lng === _southWest.lng &&
				this.lastBounds._northEast.lat === _northEast.lat &&
				this.lastBounds._northEast.lng === _northEast.lng &&
				!force) return;
				this.lastBounds = { _northEast, _southWest };


				/* panning potential: get ids from visible markers
			let ids = [];
			for (let markerData of Object.values(this.createdMarkers)) {
				if (this.state.map.getBounds().contains(markerData.marker.getLatLng())) {
					ids = ids.concat(markerData.orderIds);
				}
			}
			*/

				let nodes = this.getClusterNodes(this.mapCluster);
				let ordersForStore = nodes.reduce( (acc, { properties }) => acc.concat(properties.orderIds || [properties.id]), []);
				if (!this.checkArraysSame(this.mapOrderIds, ordersForStore)) this.ordersInMap(ordersForStore);
				return;
			} catch (e) {
				console.log(e);
				return;
			}
		},
		getClusterNodes(cluster) {
			if (!this.state.map) return [];
			let { _northEast, _southWest } = this.state.map.getBounds();
			// SuperCluster expects round zoom values.
			return Object.freeze(cluster.getClusters([ _southWest.lng, _southWest.lat, _northEast.lng, _northEast.lat ], Math.floor(this.state.map.getZoom())));
		},
		getVisibleOrderNodes(reload) {
			if (reload) {
				let { features } = this.clusterPoints;
				if (this.selectedGridOrderIds.length > 0) {
					let lookup = new Set(this.selectedGridOrderIds);
					features = features.filter(x => lookup.has(x.properties.id));
				}

				if (this.filteredGridOrderIds) {
					if (this.filteredGridOrderIds.length > 0) {
						let lookup = new Set(this.filteredGridOrderIds);
						features = features.filter(x => lookup.has(x.properties.id));
					} else {
						features = [];
					}
				}
				this.cluster.load(features);
			}
			//return Object.freeze(this.cluster.getClusters([-175, -85, 180, 175], Math.floor(this.state.map.getZoom())));
			return this.getClusterNodes(this.cluster);
		},
		async createNodes(reload) {
			this.clearPopupData();
			this.resetSelectedMarkers(true);
			let nodes = this.getVisibleOrderNodes(reload);
			let markers = [];

			let baseOrderIcon = this.orderIcon;
			let baseTerminalIcon = this.terminalIcon;
			let createMarkerIcon= this.createIcon;
			let currentOrders = this.orderData;

			for (let { latitude, longitude, ...order } of
				nodes = Object.freeze(nodes.map(({ geometry: { coordinates }, properties }) => ({ latitude: coordinates[1], longitude: coordinates[0], ...properties })))
			) {
				let icon = null;
				let orderIdsSet = new Set(order.orderIds);
				if ( order.cluster && orderIdsSet.size > 1 && !order.terminals.length ) {
					icon = createMarkerIcon({
						iconName: 'order',
						iconType: 'timpex',
						cluster: order
					});
				} else if ( order.terminals && order.terminals.length ) {
					icon = createMarkerIcon({
						iconName: 'office',
						iconType: 'bp',
						cluster: order
					});
				} else if ( order.type === 'terminal') {
					icon = baseTerminalIcon;
				} else {
					icon = baseOrderIcon;
				}

				let { status } = order;
				if (order.cluster && orderIdsSet.size === 1) {
					let singleOrder = currentOrders.find(x => x.id === order.orderIds[0]);
					status = singleOrder.status;
				}

				let iconColor = order.deliveryMarker ? 'dark-neutral' : this.getStatusColor(status);

				let options = { draggable: !this.tripDetailView, icon };
				if (this.pane) options.pane = this.pane;
				let marker = this.leaflet.marker([ latitude, longitude ], options);
				let key = `${latitude}:${longitude}`;

				let markerClickable = order.orderId || (order.cluster && orderIdsSet.size > 1 && !order.terminals.length ) || (order.terminals && order.terminals.length);
				if (markerClickable) {
					marker.on('click', (event) => this._click(event, key));
				}
				if (order.orderId) {
					marker.on('dblclick', (event) => this._dblclick(event, key));
				}
				marker.on('add', (event) => this._add(event, key));
				//marker.on('contextmenu', (event) => this._contextmenu(event, key));
				marker.on('dragstart', (event) => this._dragstart(event, key));
				marker.on('mouseover', (event) => this.toggleTooltip(event, key, true));
				marker.on('mouseout', (event) => this.toggleTooltip(event, key, false));

				this.createdMarkers[key] = {
					id: key,
					marker,
					orderId: order.orderId,
					coordinates: [ latitude, longitude ],
					latitude,
					longitude,
					cluster: order.cluster,
					cluster_id: order.cluster_id,
					deliveryMarker: order.deliveryMarker,
					orderIds: order.orderIds || [order.id],
					icon,
					selected: false,
					iconColor: order.cluster && orderIdsSet.size > 1 ? 'blue' : iconColor,
					clickTimeout: null,
					context: this,
					disableClick: !markerClickable
				};
				markers.push(marker);
			}
			await this.$nextTick();
			this.$refs.layerGroup.addLayers(markers);
		},
		setupTerminalPoints() {
			let points = {
				type: 'FeatureCollection',
				features: []
			};

			this.terminals.forEach(item => {
				if (item.latitude && item.longitude) {
					points.features.push({
						type: 'Feature',
						id: item.id,
						geometry: {
							type: 'Point',
							coordinates: [ item.longitude, item.latitude ]
						},
						properties: { id: item.id, type: 'terminal' }
					});
				}
			});

			this.validTerminals = Object.freeze(points.features);
		},
		getStatusColor(status) {
			return statuses.color(status);
		},
		checkArraysSame(arr1, arr2) {
			if (arr1 === arr2) return true;
			if (arr1 === null || arr2 === null || arr1.length !== arr2.length) return false;
			let lookup = new Set(arr2);
			for (let i = 0; i < arr1.length; i++) {
				if (!lookup.has(arr1[i])) return false;
			}
			return true;
		},
		addMarkerClass(marker, className) {
			if (marker && marker._icon)
				this.leaflet.DomUtil.addClass(marker._icon, className);
		},
		removeMarkerClass(marker, className) {
			if (marker && marker._icon)
				this.leaflet.DomUtil.removeClass(marker._icon, className);
		},
		onMarkerClick(event, markerData) {
			if (!this.tripDetailView) this.setTripDetails(null);
			if (this.dragging || !this.modal) return;
			let multiSelect = event.originalEvent.shiftKey;
			this.clearPopupData();
			if (multiSelect && !this.gridSelecting) {
				if (!markerData.selected) {
					this.selectedMarkers.push(markerData.id);
					this.addMarkerClass(markerData.marker, 'selected');
				}
			} else if (markerData.selected) {
				this.removeMarkerClass(markerData.marker, 'selected');
				let selectedIndex = this.selectedMarkers.find(x => x === markerData.id);
				if (selectedIndex > -1)
					this.selectedMarkers.splice(selectedIndex,1);
			} else {
				this.resetSelectedMarkers();
				if (this.tooltip) this.tooltip = false;
				if (markerData.cluster && markerData.orderIds.length !== 1) {
					let lookup = new Set(markerData.orderIds);
					this.orderCluster = this.orderData.filter(x => lookup.has(x.id));
					this.clusterTooltip = false;
				} else {
					this.order = this.orderData.find(x => x.id === markerData.orderIds[0]);
				}
				this.addMarkerClass(markerData.marker, 'selected');
				this.selectedMarkers.push(markerData.id);
			}
			this.refreshSelectedOrders();
		},
		_add(event, markerIndex) {
			let markerData = this.createdMarkers[markerIndex];
			if (!markerData) return;
			if (markerData.iconColor) this.addMarkerClass(markerData.marker, markerData.iconColor);
		},
		_click(event, markerIndex) {
			let markerData = this.createdMarkers[markerIndex];
			if (!markerData || markerData.disableClick) return;
			clearTimeout(markerData.clickTimeout);
			markerData.clickTimeout = setTimeout(() => {
				this.onMarkerClick(event, markerData, markerIndex);
			},200);
		},
		_dblclick(event, markerIndex) {
			let markerData = this.createdMarkers[markerIndex];
			if (!markerData || !markerData.orderId) return;

			clearTimeout(markerData.clickTimeout);
			eventBus.$emit('setEditOrder', markerData.orderId);
		},
		_dragstart(event, markerIndex) {
			let markerData = this.createdMarkers[markerIndex];
			if (!markerData) return;

			this.dragging = true;
			this.draggedIndex = markerIndex;

			if (this.tooltip || this.clusterTooltip) {
				this.tooltip = false;
				this.clusterTooltip = false;
				this.state.map.closePopup(this.popupComponent);
			}

			this.clonedNode = markerData.marker._icon.cloneNode(true);
			this.clonedNode.style.position = 'fixed';
			this.clonedNode.style.zIndex = '99999';
			this.clonedNode.style.pointerEvents = 'none';
			this.clonedNode.style.transform = '';

			markerData.marker.setOpacity(0);

			document.body.appendChild(this.clonedNode);
			document.body.style.cursor = 'grab';

			window.addEventListener('mousemove', this.drag);
			window.addEventListener('mouseup', this._drop);
		},
		drag(e) {
			this.clonedNode.style.left = `${e.clientX}px`;
			this.clonedNode.style.top = `${e.clientY}px`;
		},
		_drop() {
			window.removeEventListener('mousemove', this.drag);
			window.removeEventListener('mouseup', this._drop);

			let markerData = this.createdMarkers[this.draggedIndex];
			if (!markerData) return;

			document.body.style.cursor = '';
			document.body.style.userSelect = '';
			document.body.removeChild(this.clonedNode);
			this.clonedNode = null;

			markerData.marker.setOpacity(1);
			markerData.marker.setLatLng([ markerData.latitude, markerData.longitude ]);

			this.dragging = false;
			this.draggedIndex = null;
			let ret = null;
			if (this.mapSelecting) {
				let lookup = new Set(this.selectedMapOrderIds);
				ret = this.orderData.filter(x => lookup.has(x.id));
			} else {
				let lookup = new Set(markerData.orderIds);
				ret = this.orderData.filter(x => lookup.has(x.id));
			}

			this.$store.commit('draggable/drop', ret);
			this.$store.commit('draggable/dragging', false);
		},
		toggleTooltip(event, markerIndex, entering) {
			if (this.dragging) return;
			if (!this.modal) return;

			let marker = this.createdMarkers[markerIndex];
			if (!marker) return;
			if (!this.popupComponent) this.createPopup();
			if (!entering) {
				if (this.clusterTooltip) {
					this.clusterTooltip = false;
					this.orderCluster = null;
				}
				if (this.tooltip) {
					this.tooltip = false;
					this.order = {};
				}
				this.state.map.closePopup(this.popupComponent);
			} else if (entering) {
				if (marker.cluster_id && marker.orderIds.length !== 1) {
					let lookup = new Set(marker.orderIds);
					this.orderCluster = this.orderData.filter(x => lookup.has(x.id));
					this.clusterTooltip = true;
					if (this.tooltip) {
						this.tooltip = false;
						this.order = {};
					}
					this.state.map.openPopup(this.popupComponent, marker.coordinates);
				} else {
					this.order = this.orderData.find(x => x.id === marker.orderIds[0]);
					if (!this.order) { this.tooltip = false; return ; }
					this.tooltip = true;
					if (this.clusterTooltip) {
						this.clusterTooltip = false;
						this.orderCluster = null;
					}
					this.state.map.openPopup(this.popupComponent, marker.coordinates);
				}
			}
		},
		spiderOrdersWIP(markerData) {
			let destinationPoint = function(lat, lng, brng, dist) {
				dist = dist / 6371;
				brng = brng * Math.PI / 180;
				let lat1 = lat * Math.PI / 180;
				let lon1 = lng * Math.PI / 180;
				let lat2 = Math.asin(
					Math.sin(lat1) * Math.cos(dist) +
					Math.cos(lat1) * Math.sin(dist) * Math.cos(brng)
				);
				let lon2 = lon1 + Math.atan2(
					Math.sin(brng) * Math.sin(dist) * Math.cos(lat1),
					Math.cos(dist) - Math.sin(lat1) * Math.sin(lat2)
				);
				if (isNaN(lat2) || isNaN(lon2)) return null;
				return [ lat2 * 180 / Math.PI, lon2 * 180 / Math.PI ];
			};

			// in kilometers
			let mapToZoomValue = function(latitude, longitude) {
				let { x, y } = this.state.map.project([ latitude, longitude ], this.state.map.getZoom());
				let distance = this.state.map.distance(
					this.state.map.unproject([ x + 30, y + 30 ], this.state.map.getZoom()),
					[ latitude, longitude ]
				);
				return distance / 1000;
			}.bind(this);

			this.addMarkerClass(markerData.marker, 'selected');
			this.selectedMarkers.push(markerData.id);

			let spiderOrders = this.cluster.getChildren(markerData.cluster_id).map(({ properties, geometry: { coordinates } }) => ({ ...properties, coordinates }));
			//[ order.despatchParty.latitude, order.despatchParty.longitude ]

			let angle = 0;
			let factor = 1;

			let spiderMarkers = [];
			let spiderLines = [];

			for (let order of spiderOrders) {
				let [ latitude, longitude ] = order.coordinates;
				let point = destinationPoint(latitude, longitude, angle, mapToZoomValue(latitude, longitude, 40, factor));

				spiderLines.push({
					id: `line-${markerData.coordinates[0]}:${markerData.coordinates[1]}-${point[0]}:${point[1]}`,
					points: [
						point,
						markerData.coordinates
					]
				});

				spiderMarkers.push({
					id: `marker-${markerData.coordinates[0]}:${markerData.coordinates[1]}-${point[0]}:${point[1]}`,
					orderId: order.orderId,
					coordinates: point,
					latitude: point[0],
					longitude: point[1],
					cluster: order.cluster,
					cluster_id: order.cluster_id,
					pickup_count: order.pickup_count,
					delivery_count: order.delivery_count,
					deliveryMarker: order.deliveryMarker,
					orderIds: order.orderIds || [order.id],
					icon: this.createIcon({
						iconName: 'order',
						iconType: 'timpex',
						background: '#FFFFFF',
						color: order.deliveryMarker ? '#234361' : '#1070ca',
						fillColor: order.deliveryMarker ? '#7B8B9A' : '#ddebf7',
						cluster: order.cluster && order.orderIds.length > 1 ? order : null
					}),
					selected: false,
					clickTimeout: null,
					context: this,
				});
				angle += 45 / factor;

				if (angle >= 360) {
					factor += 1;
					angle = 0;
				}
			}
			this.spiderLines = Object.freeze(spiderLines);
			this.spiderMarkers = Object.freeze(spiderMarkers);
		},
	},
};
</script>
